/**
 * spaceSpriteManifest.ts
 * Single source of truth for the World 2 space-scene sprite assets
 * referenced by the entity / background draw paths (asteroids, drone
 * enemies, homing mines, parallax starfield).
 *
 * Co-locating the URL imports here:
 *
 *   1. Keeps Vite asset resolution centralised — a rename or move of
 *      any of these PNGs only edits one file.
 *   2. Pairs each spriteId emitted by `spaceRender.ts` /
 *      `spaceDrawBuilders.ts` with a concrete asset, so a new id in
 *      the draw list with no matching entry here is a TypeScript error
 *      (`Record<SpaceEntitySpriteId, string>` is exhaustive).
 *   3. Lets a unit test import the manifest and assert each entry
 *      resolves to a non-empty URL — the boot smoke regression that
 *      AGENTS.md mandates whenever a static asset import is added.
 *
 * NO React imports, NO async — pure constants.
 *
 * Phase 4 — World 2: Starfield Drift (polish pass).
 *
 * @module spaceSpriteManifest
 */

/**
 * Union of every entity sprite id emitted by the space-scene draw list
 * builders. The id format mirrors the field that drives it:
 *
 *   - `asteroid-<size>`   from `a.size` (`AsteroidSize`)
 *   - `enemy-<kind>`      from `e.kind`  (`SpaceEnemyKind`)
 *   - `mine`              fixed — both IDLE and ACTIVE share one sprite
 *   - `shield-node`       fixed — boss shield-ring orbiting node
 *
 * Adding a new asteroid size or enemy kind requires growing both this
 * union AND the matching entry in {@link SPACE_ENTITY_SPRITE_MODULES}.
 */
export type SpaceEntitySpriteId =
  | 'asteroid-sm'
  | 'asteroid-md'
  | 'asteroid-lg'
  | 'asteroid-xl'
  | 'enemy-fighter'
  | 'enemy-bomber'
  | 'enemy-satellite'
  | 'enemy-starEater'
  | 'mine'
  | 'shield-node';

import spaceBgFarUrl from '../../assets/sprites/bg-world2-far.png';
import spaceBgFarV1Url from '../../assets/sprites/bg-world2-far-v1.png';
import spaceBgFarV2Url from '../../assets/sprites/bg-world2-far-v2.png';
import spaceBgFarV3Url from '../../assets/sprites/bg-world2-far-v3.png';
import spaceBgFarV4Url from '../../assets/sprites/bg-world2-far-v4.png';
import asteroidSmUrl from '../../assets/sprites/asteroid-sm.png';
import asteroidMdUrl from '../../assets/sprites/asteroid-md.png';
import asteroidLgUrl from '../../assets/sprites/asteroid-lg.png';
import asteroidXlUrl from '../../assets/sprites/asteroid-xl.png';
import enemyFighterUrl from '../../assets/sprites/fighter-drone.png';
import enemyBomberUrl from '../../assets/sprites/enemy-bomber.png';
import enemySatelliteUrl from '../../assets/sprites/enemy-satellite.png';
import enemyStarEaterUrl from '../../assets/sprites/enemy-star-eater.png';
import mineUrl from '../../assets/sprites/homing-mine.png';
import shieldNodeUrl from '../../assets/sprites/void-mothership-shield-node.png';

// DECISION: Vite static-asset imports replace the original
// `require('...png')` calls. The browser has no `require`, so the
// originals threw at module-load time and killed the whole bundle
// (silent regression from the ADR-011 React Native → Vite migration).

/**
 * URL for the painted starfield background — original variant. Kept as
 * the default export so older callers / tests stay green; new render
 * code should consume {@link SPACE_BG_FAR_MODULES} instead.
 */
export const SPACE_BG_FAR_MODULE: string = spaceBgFarUrl;

/**
 * Pool of painted starfield variants used by the World 2 background
 * renderer. One variant is picked per session so the scene does not
 * always show the same backdrop. Order: original starfield-with-planet,
 * then four PixelLab variants (navy/teal nebula, purple/magenta nebula,
 * black/gold nebula, teal/emerald nebula).
 */
export const SPACE_BG_FAR_MODULES: readonly string[] = [
  spaceBgFarUrl,
  spaceBgFarV1Url,
  spaceBgFarV2Url,
  spaceBgFarV3Url,
  spaceBgFarV4Url,
];

/**
 * URL table keyed by `SpaceEntitySpriteId`. Exhaustive by construction
 * — the `Record` annotation makes a missing entry a compile error.
 */
export const SPACE_ENTITY_SPRITE_MODULES: Record<SpaceEntitySpriteId, string> = {
  'asteroid-sm': asteroidSmUrl,
  'asteroid-md': asteroidMdUrl,
  'asteroid-lg': asteroidLgUrl,
  'asteroid-xl': asteroidXlUrl,
  'enemy-fighter': enemyFighterUrl,
  'enemy-bomber': enemyBomberUrl,
  'enemy-satellite': enemySatelliteUrl,
  'enemy-starEater': enemyStarEaterUrl,
  mine: mineUrl,
  'shield-node': shieldNodeUrl,
};

